import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function DashboardEmpresa({ usuario, onDrillDown, onDrillDownLicencias }) {
    const [stats, setStats] = useState({
        tipos: 0,
        contratistas: 0,
        personal: 0,
        vehiculos: 0,
        documentos_total: 0,
        documentos_invalidos: 0,
        licencias_medicas_12m: 0,
        licencias_medicas_vigentes: 0
    });
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        const theme = document.documentElement.getAttribute('data-theme') || 'light';
        setIsDarkMode(theme === 'dark');

        const observer = new MutationObserver(() => {
            const newTheme = document.documentElement.getAttribute('data-theme') || 'light';
            setIsDarkMode(newTheme === 'dark');
        });

        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                
                if (!usuario?.empresa_id) {
                    setError('No se encontró ID de empresa');
                    setLoading(false);
                    return;
                }

                // Fetch stats
                const statsResponse = await fetch(`${API_BASE_URL}/api/empresas/${usuario.empresa_id}/dashboard-stats`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                // Fetch timeline
                const timelineResponse = await fetch(`${API_BASE_URL}/api/empresas/${usuario.empresa_id}/documents-timeline`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (statsResponse.ok && timelineResponse.ok) {
                    const statsData = await statsResponse.json();
                    const timelineData = await timelineResponse.json();

                    setStats(statsData);

                    // Format timeline data for chart
                    const formattedTimeline = timelineData.map(item => ({
                        fecha: new Date(item.fecha).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
                        cantidad: parseInt(item.cantidad)
                    }));

                    setTimeline(formattedTimeline);
                } else {
                    setError('Error al cargar datos del dashboard');
                }
            } catch (err) {
                console.error('Error en DashboardEmpresa:', err);
                setError('Error de conexión');
            } finally {
                setLoading(false);
            }
        };

        if (usuario?.empresa_id) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [usuario]);

    if (loading) return <div className="dashboard-loading">Cargando resumen...</div>;

    if (error) return <div className="error-message" style={{ marginTop: '20px' }}>{error}</div>;

    return (
        <>
            <div className="dashboard-grid">
                <div className="stat-card">
                    <div className="stat-icon">📋</div>
                    <div className="stat-info">
                        <h4>Documentos Definidos</h4>
                        <div className="stat-value">{stats.tipos}</div>
                        <p>Tipos de documentos activos</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🏗️</div>
                    <div className="stat-info">
                        <h4>Contratistas</h4>
                        <div className="stat-value">{stats.contratistas}</div>
                        <p>Empresas vinculadas</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                        <h4>Total Personal</h4>
                        <div className="stat-value">{stats.personal}</div>
                        <p>Trabajadores en sistema</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">🚗</div>
                    <div className="stat-info">
                        <h4>Vehículos</h4>
                        <div className="stat-value">{stats.vehiculos}</div>
                        <p>Vehículos registrados</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">📄</div>
                    <div className="stat-info">
                        <h4>Documentos Procesados</h4>
                        <div className="stat-value">{stats.documentos_total}</div>
                        <p>Total de documentos</p>
                    </div>
                </div>
                <div
                    className="stat-card"
                    style={{
                        borderLeft: '4px solid #ef4444',
                        cursor: onDrillDown ? 'pointer' : 'default',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                    }}
                    onClick={() => onDrillDown && onDrillDown('pendientes')}
                    onMouseEnter={e => { if (onDrillDown) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } }}
                    onMouseLeave={e => { if (onDrillDown) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } }}
                    title={onDrillDown ? 'Click para ver documentos pendientes' : ''}
                >
                    <div className="stat-icon">⚠️</div>
                    <div className="stat-info">
                        <h4>Documentos Pendientes</h4>
                        <div className="stat-value" style={{ color: '#ef4444' }}>{stats.documentos_invalidos}</div>
                        <p>Requieren atención{onDrillDown && ' · Click para ver'}</p>
                    </div>
                </div>
                <div
                    className="stat-card"
                    style={{
                        borderLeft: '4px solid #10b981',
                        cursor: onDrillDownLicencias ? 'pointer' : 'default',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                    }}
                    onClick={() => onDrillDownLicencias && onDrillDownLicencias('12m')}
                    onMouseEnter={e => { if (onDrillDownLicencias) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } }}
                    onMouseLeave={e => { if (onDrillDownLicencias) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } }}
                    title={onDrillDownLicencias ? 'Click para ver licencias de los últimos 12 meses' : ''}
                >
                    <div className="stat-icon">🏥</div>
                    <div className="stat-info">
                        <h4>Licencias Médicas</h4>
                        <div className="stat-value" style={{ color: '#10b981' }}>{stats.licencias_medicas_12m}</div>
                        <p>Últimos 12 meses{onDrillDownLicencias && ' · Click para ver'}</p>
                    </div>
                </div>
                <div
                    className="stat-card"
                    style={{
                        borderLeft: '4px solid #0ea5e9',
                        cursor: onDrillDownLicencias ? 'pointer' : 'default',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                    }}
                    onClick={() => onDrillDownLicencias && onDrillDownLicencias('vigentes')}
                    onMouseEnter={e => { if (onDrillDownLicencias) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } }}
                    onMouseLeave={e => { if (onDrillDownLicencias) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } }}
                    title={onDrillDownLicencias ? 'Click para ver licencias vigentes' : ''}
                >
                    <div className="stat-icon">✅</div>
                    <div className="stat-info">
                        <h4>Licencias Vigentes</h4>
                        <div className="stat-value" style={{ color: '#0ea5e9' }}>{stats.licencias_medicas_vigentes}</div>
                        <p>Válidas y no vencidas{onDrillDownLicencias && ' · Click para ver'}</p>
                    </div>
                </div>
            </div>

            {timeline.length > 0 && (
                <div className="dashboard-card" style={{
                    marginTop: '32px',
                    padding: '24px',
                    background: isDarkMode ? 'var(--bg-card)' : '#ffffff',
                    borderRadius: '12px',
                    boxShadow: isDarkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid var(--border-light)'
                }}>
                    <h3 style={{
                        marginBottom: '24px',
                        color: isDarkMode ? 'var(--text-main)' : 'var(--sidebar-bg)',
                        fontSize: '1.25rem',
                        fontWeight: '600'
                    }}>
                        📊 Documentos Procesados - Últimos 6 Meses
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid 
                                strokeDasharray="3 3" 
                                stroke={isDarkMode ? '#334155' : '#e2e8f0'} 
                            />
                            <XAxis
                                dataKey="fecha"
                                stroke={isDarkMode ? '#94a3b8' : '#64748b'}
                                style={{ fontSize: '0.875rem' }}
                            />
                            <YAxis
                                stroke={isDarkMode ? '#94a3b8' : '#64748b'}
                                style={{ fontSize: '0.875rem' }}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                                    border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                    borderRadius: '8px',
                                    boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)'
                                }}
                                labelStyle={{ color: isDarkMode ? '#f1f5f9' : '#0f172a', fontWeight: '600' }}
                            />
                            <Line
                                type="monotone"
                                dataKey="cantidad"
                                stroke="#4f46e5"
                                strokeWidth={2}
                                dot={{ fill: '#4f46e5', r: 4 }}
                                activeDot={{ r: 6 }}
                                name="Documentos"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </>
    );
}
